import { useEffect, useRef } from 'react';

const VISIBILITY_THRESHOLD = 0.5;
const VISIBILITY_DURATION = 2000;
const viewedTeasers = new Set();
const viewedTeasersByBlock = new Map();
const sentTeasersByBlock = new Map();
const pendingTeasersByBlock = new Map();
const blockSizes = new Map();

let wasCollectSent = false;

const sendBlockData = (blockType, teasersToSend, blockId, showId, campaignIds, creativeIds) => {
    const sentTeasers = sentTeasersByBlock.get(blockType) || new Set();
    teasersToSend.forEach(teaser => sentTeasers.add(teaser.id));
    sentTeasersByBlock.set(blockType, sentTeasers);

    const isFirst = !wasCollectSent;
    const visUrl = isFirst ? '/api/ads/collect' : '/api/ads/collect_teaser';
    wasCollectSent = true;

    const teaserIds = teasersToSend.map(teaser => Number(teaser.id));
    const paramsObj = {
        obj: blockId.toString(),
        teaser_ids: teaserIds.join(','),
        show_id: showId.toString()
    };
    if (!isFirst) {
        const campaignIdsNum = campaignIds.map(Number);
        const creativeIdsStr = creativeIds.map(String);
        paramsObj.campaign_ids = campaignIdsNum.join(',');
        paramsObj.creative_ids = creativeIdsStr.join(',');
    }
    const params = new URLSearchParams(paramsObj);
    
    fetch(`${visUrl}?${params.toString()}`, {
        method: 'GET',
        mode: 'no-cors',
        keepalive: true
    }).catch(error => {
        console.error('Ошибка при отправке данных о видимости тизеров:', error);
    });
};

const checkAndSendBlockData = (blockType, teaser, blockId, showId, campaignIds, creativeIds) => {
    let alreadyPending = false;
    pendingTeasersByBlock.forEach((teasersSet, block) => {
        if (block !== blockType) {
            for (let t of teasersSet) {
                if (t.id === teaser.id) {
                    alreadyPending = true;
                }
            }
        }
    });
    if (alreadyPending) return;

    const pendingTeasers = pendingTeasersByBlock.get(blockType) || new Set();
    const blockSize = blockSizes.get(blockType);
    const teaserWithContext = {
        ...teaser,
        blockId,
        showId,
        campaignIds: Array.isArray(campaignIds) ? campaignIds.map(Number) : [Number(campaignIds)],
        creativeIds: Array.isArray(creativeIds) ? creativeIds.map(String) : [String(creativeIds)]
    };
    if (blockType.includes('Feed')) {
        pendingTeasers.add(teaserWithContext);
        pendingTeasersByBlock.set(blockType, pendingTeasers);
        if (pendingTeasers.size >= 5) {
            const allCampaignIds = Array.from(pendingTeasers).map(t => t.campaignIds).flat();
            const allCreativeIds = Array.from(pendingTeasers)
                .map(t => Array.isArray(t.creativeIds) ? t.creativeIds : [t.creativeIds])
                .flat()
                .map(String);
            sendBlockData(blockType, Array.from(pendingTeasers), blockId, showId, allCampaignIds, allCreativeIds);
            pendingTeasers.clear();
            pendingTeasersByBlock.set(blockType, pendingTeasers);
        }
        return;
    }
    pendingTeasers.add(teaserWithContext);
    pendingTeasersByBlock.set(blockType, pendingTeasers);
    if (pendingTeasers.size >= blockSize) {
        const allCampaignIds = Array.from(pendingTeasers).map(t => t.campaignIds).flat();
        const allCreativeIds = Array.from(pendingTeasers)
            .map(t => Array.isArray(t.creativeIds) ? t.creativeIds : [t.creativeIds])
            .flat()
            .map(String);
        sendBlockData(blockType, Array.from(pendingTeasers), blockId, showId, allCampaignIds, allCreativeIds);
        pendingTeasers.clear();
        pendingTeasersByBlock.set(blockType, pendingTeasers);
    }
};

if (typeof window !== 'undefined') {
    const sendPendingTeasers = () => {
        pendingTeasersByBlock.forEach((teasers, blockType) => {
            if (teasers.size > 0) {
                const groupedTeasers = Array.from(teasers).reduce((acc, teaser) => {
                    const key = `${teaser.blockId}_${teaser.showId}`;
                    if (!acc[key]) {
                        acc[key] = {
                            blockId: teaser.blockId,
                            showId: teaser.showId,
                            teasers: [],
                            campaignIds: [],
                            creativeIds: []
                        };
                    }
                    acc[key].teasers.push(teaser);
                    if (teaser.campaignIds) {
                        if (Array.isArray(teaser.campaignIds)) {
                            acc[key].campaignIds.push(...teaser.campaignIds.map(Number));
                        } else {
                            acc[key].campaignIds.push(Number(teaser.campaignIds));
                        }
                    }
                    if (teaser.creativeIds) {
                        acc[key].creativeIds.push(...teaser.creativeIds);
                    }
                    return acc;
                }, {});

                Object.values(groupedTeasers).forEach(group => {
                    group.creativeIds = group.creativeIds.flat().map(String);
                    group.campaignIds = group.campaignIds.flat().map(Number);
                    sendBlockData(blockType, group.teasers, group.blockId, group.showId, group.campaignIds, group.creativeIds);
                });
                teasers.clear();
                pendingTeasersByBlock.set(blockType, teasers);
            }
        });
    };

    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            sendPendingTeasers();
        }
    });

    window.addEventListener('pagehide', sendPendingTeasers);

    window.addEventListener('beforeunload', sendPendingTeasers);
}

export const useVisibilityTracker = (teaserId, blockId, blockType, blockSize, showId, campaignIds, creativeIds) => {
    const elementRef = useRef(null);
    const timerRef = useRef(null);
    const isVisibleRef = useRef(false);

    if (blockSize && !blockSizes.has(blockType)) {
        blockSizes.set(blockType, blockSize);
    }

    useEffect(() => {
        if (!elementRef.current || viewedTeasers.has(teaserId)) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const entry = entries[0];
                const isVisible = entry.isIntersecting && 
                    entry.intersectionRatio >= VISIBILITY_THRESHOLD;

                if (isVisible && !isVisibleRef.current) {
                    isVisibleRef.current = true;
                    timerRef.current = setTimeout(() => {
                        if (isVisibleRef.current && !viewedTeasers.has(teaserId)) {
                            viewedTeasers.add(teaserId);
                            
                            if (!viewedTeasersByBlock.has(blockType)) {
                                viewedTeasersByBlock.set(blockType, new Set());
                            }
                            viewedTeasersByBlock.get(blockType).add(teaserId);

                            checkAndSendBlockData(blockType, {id: teaserId}, blockId, showId, campaignIds, creativeIds);
                        }
                    }, VISIBILITY_DURATION);
                } else if (!isVisible && isVisibleRef.current) {
                    isVisibleRef.current = false;
                    if (timerRef.current) {
                        clearTimeout(timerRef.current);
                    }
                }
            },
            {
                threshold: VISIBILITY_THRESHOLD
            }
        );

        observer.observe(elementRef.current);

        return () => {
            observer.disconnect();
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
            const pendingTeasers = pendingTeasersByBlock.get(blockType);
            if (pendingTeasers && pendingTeasers.size > 0) {
                const allCampaignIds = Array.from(pendingTeasers)
                    .map(t => Array.isArray(t.campaignIds) ? t.campaignIds : [t.campaignIds])
                    .flat()
                    .map(Number);
                const allCreativeIds = Array.from(pendingTeasers)
                    .map(t => Array.isArray(t.creativeIds) ? t.creativeIds : [t.creativeIds])
                    .flat()
                    .map(String);
                sendBlockData(blockType, Array.from(pendingTeasers), blockId, showId, allCampaignIds, allCreativeIds);
                pendingTeasers.clear();
                pendingTeasersByBlock.set(blockType, pendingTeasers);
            }
        };
    }, [teaserId, blockId, blockType, showId]);

    return elementRef;
};